/**
 * ScoringProfileEditor — user-editable scoring profile (2026-05-06).
 *
 * Filozofia: nie hardcoduj branż. User wpisuje własne keywords/CPV/exclude,
 * system dostosowuje match score. Adam = wszechstronny biznesmen, nie tylko handlowiec.
 *
 * 4 listy edytowalne: primary CPV, adjacent CPV, tender keywords, exclude keywords.
 * Plus: industry template (preset) + value bonus threshold.
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import {
  Settings2, Plus, X, Save, Sparkles, Tag, ShieldOff, RefreshCw, Info,
} from 'lucide-react';
import { getScoringProfile, updateScoringProfile } from '../../lib/api';

function TagListEditor({ label, icon: Icon, items, onAdd, onRemove, placeholder, color = 'primary' }) {
  const [input, setInput] = useState('');
  const colorClass = {
    primary: 'bg-primary/10 text-primary border-primary/30',
    amber: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    red: 'bg-red-500/15 text-red-300 border-red-500/30',
    blue: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  }[color];

  const handleAdd = () => {
    const v = input.trim();
    if (!v) return;
    if (items.includes(v)) {
      toast.info('Już jest w liście');
      return;
    }
    onAdd(v);
    setInput('');
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Icon className="w-4 h-4" />
          {label}
          <Badge variant="outline" className="ml-auto">{items.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex gap-1.5">
          <Input
            placeholder={placeholder}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
            className="text-sm"
          />
          <Button onClick={handleAdd} size="sm" disabled={!input.trim()}>
            <Plus className="w-3.5 h-3.5" />
          </Button>
        </div>
        {items.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">— brak —</p>
        ) : (
          <div className="flex flex-wrap gap-1.5 max-h-[200px] overflow-y-auto">
            {items.map(item => (
              <span
                key={item}
                className={`text-xs px-2 py-0.5 rounded border ${colorClass} flex items-center gap-1 group`}
              >
                {item}
                <button
                  onClick={() => onRemove(item)}
                  className="opacity-50 hover:opacity-100 ml-0.5"
                  title="Usuń"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function ScoringProfileEditor() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const p = await getScoringProfile();
      setProfile(p);
      setDirty(false);
    } catch (e) {
      toast.error('Błąd ładowania profilu: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const update = (updater) => {
    setProfile(p => {
      const next = updater({ ...p });
      setDirty(true);
      return next;
    });
  };

  const handleSave = async () => {
    if (!profile) return;
    setSaving(true);
    try {
      const res = await updateScoringProfile({
        industry: profile.industry,
        primary_cpv: profile.primary_cpv,
        adjacent_cpv: profile.adjacent_cpv,
        tender_keywords: profile.tender_keywords,
        exclude_keywords: profile.exclude_keywords,
        exclude_cpv_prefix: profile.exclude_cpv_prefix,
        value_bonus_threshold_pln: profile.value_bonus_threshold_pln,
      });
      toast.success(`Zapisano profil "${res.industry}" — system będzie używał Twoich custom keywords/CPV`);
      setDirty(false);
      await load();
    } catch (e) {
      toast.error('Zapis nie powiódł się: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-20 w-full" />
        <div className="grid grid-cols-2 gap-3">
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
        </div>
      </div>
    );
  }

  if (!profile) return <Card><CardContent className="pt-6">Brak profilu</CardContent></Card>;

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Settings2 className="w-4 h-4 text-primary" />
                Profil scoringowy — co interesuje Twój biznes?
              </CardTitle>
              <CardDescription className="mt-1.5">
                System ocenia każdy z 400+ przetargów na podstawie Twoich list. Edytuj swobodnie —
                Adam = wszechstronny biznes, nie tylko jedna branża.
                {profile.is_custom && <span className="ml-1 text-emerald-400">✓ Twoje custom keywords aktywne</span>}
              </CardDescription>
            </div>
            <Button
              onClick={handleSave}
              disabled={!dirty || saving}
              className={dirty ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
              size="sm"
            >
              {saving ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              {dirty ? 'Zapisz zmiany' : 'Zapisane'}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Branża bazowa (preset)</label>
              <Select
                value={profile.industry}
                onValueChange={v => update(p => ({ ...p, industry: v }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(profile.available_industries || []).map(ind => (
                    <SelectItem key={ind.key} value={ind.key}>{ind.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Reset list do template branży. Twoje dodane wpisy zachowane jako rozszerzenie.
              </p>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Big-ticket bonus threshold (PLN)</label>
              <Input
                type="number"
                value={profile.value_bonus_threshold_pln || 0}
                onChange={e => update(p => ({ ...p, value_bonus_threshold_pln: Number(e.target.value) || 0 }))}
                placeholder="5000000"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Przetargi powyżej tej wartości dostają +15 pkt nawet jeśli Twój asortyment to mała część.
                Adam jako negocjator wchodzi w duże, wielobranżowe projekty.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Help */}
      <div className="text-xs text-muted-foreground flex gap-2 px-1">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span>
          Wzór: <strong>Score = primary CPV (max 40) + adjacent CPV (max 25) + keywords (max 30)
          + region (10) + deadline (max 20) + big-ticket bonus (15) − exclude (max 50)</strong>.
          Próg ≥70 = high, 40-69 = medium, &lt;40 = low.
        </span>
      </div>

      {/* 4 lists */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <TagListEditor
          label="Primary CPV (główne — pełne 40 pkt)"
          icon={Sparkles}
          items={profile.primary_cpv || []}
          onAdd={v => update(p => ({ ...p, primary_cpv: [...p.primary_cpv, v] }))}
          onRemove={v => update(p => ({ ...p, primary_cpv: p.primary_cpv.filter(x => x !== v) }))}
          placeholder="np. 31520000 (Lampy)"
          color="primary"
        />
        <TagListEditor
          label="Adjacent CPV (przyległe — max 25 pkt)"
          icon={Tag}
          items={profile.adjacent_cpv || []}
          onAdd={v => update(p => ({ ...p, adjacent_cpv: [...p.adjacent_cpv, v] }))}
          onRemove={v => update(p => ({ ...p, adjacent_cpv: p.adjacent_cpv.filter(x => x !== v) }))}
          placeholder="np. 45310000 (Elektryka)"
          color="blue"
        />
        <TagListEditor
          label="Tender keywords (max 30 pkt, 5/słowo)"
          icon={Sparkles}
          items={profile.tender_keywords || []}
          onAdd={v => update(p => ({ ...p, tender_keywords: [...p.tender_keywords, v] }))}
          onRemove={v => update(p => ({ ...p, tender_keywords: p.tender_keywords.filter(x => x !== v) }))}
          placeholder="np. modernizacja oświetlenia"
          color="amber"
        />
        <TagListEditor
          label="Exclude keywords (-30 pkt każde)"
          icon={ShieldOff}
          items={profile.exclude_keywords || []}
          onAdd={v => update(p => ({ ...p, exclude_keywords: [...p.exclude_keywords, v] }))}
          onRemove={v => update(p => ({ ...p, exclude_keywords: p.exclude_keywords.filter(x => x !== v) }))}
          placeholder="np. angiograf, histeroskop"
          color="red"
        />
      </div>

      {/* Exclude CPV prefix */}
      <TagListEditor
        label="Exclude CPV prefix (gdy WSZYSTKIE tender CPV mają taki prefix → -20 pkt)"
        icon={ShieldOff}
        items={profile.exclude_cpv_prefix || []}
        onAdd={v => update(p => ({ ...p, exclude_cpv_prefix: [...p.exclude_cpv_prefix, v] }))}
        onRemove={v => update(p => ({ ...p, exclude_cpv_prefix: p.exclude_cpv_prefix.filter(x => x !== v) }))}
        placeholder="np. 33 (medyczne), 24 (chemia)"
        color="red"
      />
    </div>
  );
}
